import { action, flow, makeObservable, observable, reaction } from 'mobx';
import {
  BlackJackHandNameByType,
  CARD_OWNER,
  MAIN_HAND_TYPE,
  SPLIT_HAND_TYPE,
} from '../blackJackCore/constants';
import { betHistoryDetailsService } from '../blackJackService/betHistoryDetailsService';
import { checkHashStore } from '../../../store/checkHashStore';
import { userInfoStore } from '../../../store/userInfoStore';
import { freeBetStore } from '../../../store/freeBetStore';

class BlackJackBetsStore {
  isBetsClosed = false;

  isDoSpinStart = false;

  userMainBet = {};

  userSplitBet = {};

  isShowInsurance = false;

  isShowDecision = false;

  isRoundFinished = false;

  isRoundStarted = false;

  isStartAnimation = false;

  startedCardAnimationIndex = null;

  possibleActions = [];

  currentSpinId = null;

  activeHandType = null;

  dealerCardsInfo = {
    totalPoint: null,
    secondPoint: null,
    hasBlackJack: null,
    cards: [],
  };

  userCardsInfo = {
    main: {
      cards: [],
      totalPoint: null,
      isDouble: false,
      secondPoint: null,
      hasBlackJack: false,
      decision: false,
    },
    split: {
      cards: [],
      totalPoint: null,
      isDouble: false,
      secondPoint: null,
      hasBlackJack: false,
      decision: false,
    },
  };

  betHistoryDetails = null;

  isBlockSendDecision = false;

  winAmount = null;

  winOdd = null;

  maxWinLimit = null;

  constructor() {
    makeObservable(this, {
      winOdd: observable,
      winAmount: observable,
      userMainBet: observable,
      userSplitBet: observable,
      maxWinLimit: observable,
      isBetsClosed: observable,
      currentSpinId: observable,
      isDoSpinStart: observable,
      isShowDecision: observable,
      activeHandType: observable,
      isRoundFinished: observable,
      isStartAnimation: observable,
      possibleActions: observable,
      isRoundStarted: observable,
      isShowInsurance: observable,
      betHistoryDetails: observable,
      isBlockSendDecision: observable,
      startedCardAnimationIndex: observable,
      userCardsInfo: observable.deep,
      dealerCardsInfo: observable.deep,
      resetStore: action.bound,
      setWinOdd: action.bound,
      setWinAmount: action.bound,
      setUserMainBet: action.bound,
      resetCardsInfo: action.bound,
      setUserSplitBet: action.bound,
      setIsBetsClosed: action.bound,
      setCurrentSpinId: action.bound,
      setUserCardsInfo: action.bound,
      setMaxWinLimit: action.bound,
      setIsDoSpinStart: action.bound,
      setActiveHandType: action.bound,
      setIsShowDecision: action.bound,
      setIsRoundStarted: action.bound,
      setIsRoundFinished: action.bound,
      setPossibleActions: action.bound,
      setDealerCardsInfo: action.bound,
      setIsShowInsurance: action.bound,
      updateUserCardsInfo: action.bound,
      setIsStartAnimation: action.bound,
      setBetHistoryDetails: action.bound,
      updateDealerCardsInfo: action.bound,
      setIsBlockSendDecision: action.bound,
      setStartedCardAnimationIndex: action.bound,
      getBetHistoryDetails: flow.bound,
    });
    reaction(
      () => this.isDoSpinStart,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
      }
    );
    reaction(
      () => this.isDoSpinStart,
      (value) => {
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );
  }

  setMaxWinLimit(limit) {
    this.maxWinLimit = limit;
  }

  setWinAmount(amount) {
    this.winAmount = amount;
  }

  setWinOdd(odd) {
    this.winOdd = odd;
  }

  setIsBlockSendDecision(bool) {
    this.isBlockSendDecision = bool;
  }

  setIsRoundStarted(bool) {
    this.isRoundStarted = bool;
  }

  setBetHistoryDetails(betHistoryDetails) {
    this.betHistoryDetails = betHistoryDetails;
  }

  *getBetHistoryDetails(mainSpinId) {
    const {
      data: { result },
    } = yield betHistoryDetailsService.getBetHistory({ mainSpinId });
    checkHashStore.setSpinInfo({
      salt: result.salt,
      key: result.key,
      hash: result.hash,
    });
    this.setBetHistoryDetails(result);
  }

  setStartedCardAnimationIndex(index) {
    this.startedCardAnimationIndex = index;
  }

  setIsStartAnimation(bool) {
    this.isStartAnimation = bool;
  }

  setIsRoundFinished(bool) {
    this.isRoundFinished = bool;
  }

  setUserSplitBet(bet = null) {
    this.userSplitBet = bet;
  }

  setIsShowDecision(bool) {
    this.isShowDecision = bool;
  }

  setDealerCardsInfo(dealerCardsInfo) {
    this.dealerCardsInfo = dealerCardsInfo;
  }

  updateDealerCardsInfo({ totalPoint, openedCard, hasBlackJack, secondPoint }) {
    const darkCardIndex = this.dealerCardsInfo.cards.findIndex(
      (el) => el.index === openedCard.index
    );
    const dealerCardsModel = {
      totalPoint,
      hasBlackJack,
      secondPoint,
      cards: [...this.dealerCardsInfo.cards],
    };
    if (darkCardIndex > -1) {
      dealerCardsModel.cards.splice(darkCardIndex, 1, openedCard);
    } else {
      dealerCardsModel.cards = [...dealerCardsModel.cards, openedCard];
    }
    this.setDealerCardsInfo(dealerCardsModel);
  }

  updateUserCardsInfo({ totalPoint, openedCard, hasBlackJack, secondPoint, isDouble, decision }) {
    const userCardsInfoModel = { ...this.userCardsInfo };
    userCardsInfoModel[BlackJackHandNameByType[openedCard.handType]] = {
      totalPoint,
      cards: [
        ...userCardsInfoModel[BlackJackHandNameByType[openedCard.handType]].cards,
        openedCard,
      ],
      hasBlackJack,
      secondPoint,
      isDouble,
      decision,
    };
    this.setUserCardsInfo(userCardsInfoModel);
  }

  setUserCardsInfo(userCardsInfo) {
    this.userCardsInfo = userCardsInfo;
  }

  setActiveHandType(type) {
    this.activeHandType = type;
  }

  setCurrentSpinId(currentSpinId) {
    this.currentSpinId = currentSpinId;
  }

  setPossibleActions(possibleActions) {
    this.possibleActions = possibleActions;
  }

  setIsShowInsurance(bool) {
    this.isShowInsurance = bool;
  }

  setUserMainBet(bet) {
    this.userMainBet = bet;
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  setIsDoSpinStart(bool) {
    this.isDoSpinStart = bool;
  }

  updateCards({
    totalPoint,
    openedCard,
    isDouble = false,
    hasBlackJack = false,
    secondPoint,
    decision,
  }) {
    if (openedCard.owner === CARD_OWNER.Dealer) {
      this.updateDealerCardsInfo({ totalPoint, openedCard, hasBlackJack, secondPoint });
    } else {
      this.updateUserCardsInfo({
        totalPoint,
        openedCard,
        hasBlackJack,
        secondPoint,
        isDouble,
        decision,
      });
    }
  }

  splitUserCards(cardInfo) {
    const userCards = this.userCardsInfo.main.cards;
    const splitBet = cardInfo.find((el) => el.spinId !== this.userMainBet.id);
    const userCardsModel = {
      [BlackJackHandNameByType[MAIN_HAND_TYPE]]: {
        ...this.userCardsInfo[BlackJackHandNameByType[MAIN_HAND_TYPE]],
        cards: [userCards[0]],
      },
      [BlackJackHandNameByType[SPLIT_HAND_TYPE]]: {
        ...this.userCardsInfo[BlackJackHandNameByType[SPLIT_HAND_TYPE]],
        cards: [userCards[1]],
      },
    };
    this.setUserCardsInfo(userCardsModel);
    this.setUserSplitBet({ ...splitBet, id: splitBet.spinId });
  }

  resetCardsInfo() {
    this.userCardsInfo = {
      main: {
        cards: [],
        totalPoint: null,
        isDouble: false,
        secondPoint: null,
        hasBlackJack: false,
        decision: false,
      },
      split: {
        cards: [],
        totalPoint: null,
        isDouble: false,
        secondPoint: null,
        hasBlackJack: false,
        decision: false,
      },
    };
    this.dealerCardsInfo = {
      totalPoint: null,
      secondPoint: null,
      hasBlackJack: null,
      cards: [],
    };
    this.isRoundFinished = false;
    this.userMainBet = {};
    this.userSplitBet = {};
  }

  resetStore() {
    this.isBetsClosed = false;

    this.isDoSpinStart = false;

    this.isShowInsurance = false;

    this.isShowDecision = false;

    this.possibleActions = [];

    this.currentSpinId = null;

    this.activeHandType = null;

    this.isRoundStarted = false;

    this.isBlockSendDecision = false;

    this.winAmount = null;
  }
}

export const blackJackBetsStore = new BlackJackBetsStore();
